import { InventorySlotType } from '@/data/enums'
import { CallbackWrap } from '@/types/common'
import { Embed, EquipmentEmbed } from '@/types/embed'
import { LevelingEmbed } from '@/types/leveling'
import { Button, Checkbox, getUniqueId, ScrollBox, Select } from '@maxroll-media-group/common-libs'
import classNames from 'classnames'
import { Input } from 'components/editor/Input'
import Paperdoll from 'components/embeds/Paperdoll'
import SkillGems from 'components/embeds/SkillGems'
import { useLevelingMetadata } from 'leveling/metadata'
import { LevelingTitleBox } from 'leveling/widgets/leveling-title-box'
import {
  NumberedMilestone,
  NumberedMilestoneGroup,
  ProgressionMilestone
} from 'leveling/widgets/progression/progression-milestone'
import { ProgressionNavGroup } from 'leveling/widgets/progression/progression-nav-group'
import { ProgressionNavGroupEditable } from 'leveling/widgets/progression/progression-nav-group-editable'
import { ProgressionNavItem } from 'leveling/widgets/progression/progression-nav-item'
import { ProgressionNavItemEditable } from 'leveling/widgets/progression/progression-nav-item-editable'
import _ from 'lodash'
import React from 'react'
import { createPortal } from 'react-dom'
import scrollIntoView from 'scroll-into-view-if-needed'
import Store, { EmbedStore } from 'store'
import { isStringInObject } from 'utils/frontend-utils'
import { useEmbed, WithStore } from '../common'
import { Widget } from '../widget'
import { PassiveTree } from '../widgets/passive-tree'
import './progression-tab.scss'

function Modal({
  onClose,
  className,
  innerClassName,
  children,
  ...props
}: {
  onClose: () => void
  innerClassName?: string
} & React.HTMLAttributes<HTMLDivElement>) {
  return typeof document !== 'undefined'
    ? createPortal(
        <div className={classNames('poe-leveling-tool-modal poeplanner-wrapper', className)}>
          <div className={classNames('poe-leveling-viewer', innerClassName)} {...props}>
            <span
              className="poe-modal-close"
              onClick={() => {
                if (onClose) {
                  onClose()
                }
              }}>
              <span></span>
            </span>
            {children}
          </div>
        </div>,
        document.querySelector('body')!
      )
    : undefined
}

export function ProgressionTab({
  embed,
  onEmbedChange,
  stores,
  metadataId,
  activeTabIndex,
  isActiveTab
}: {
  embed: LevelingEmbed
  onEmbedChange?: (value: CallbackWrap<Embed>) => void
  stores: Record<string, Store>
  metadataId: string
  activeTabIndex: number
  isActiveTab?: boolean
}) {
  const [editMilestones, setEditMilestones] = React.useState(false)

  const title = embed.tabs[activeTabIndex]?.name || 'Progression'

  const [numberedMilestones, milestonesCount] = React.useMemo(() => {
    const result: (NumberedMilestone | NumberedMilestoneGroup)[] = []
    let counter = 0
    for (const milestoneGroup of embed.milestones ?? []) {
      if ('items' in milestoneGroup) {
        const items: NumberedMilestone[] = []
        for (const milestone of milestoneGroup.items) {
          items.push({ ...milestone, index: ++counter })
        }

        result.push({ ...milestoneGroup, items: items })
      } else {
        result.push({ ...milestoneGroup, index: ++counter })
      }
    }

    return [result, counter]
  }, [embed.milestones])

  const activeTree = useEmbed(stores, embed.overview.passives, 'passives')
  const progressionEquipmentEmbed = useEmbed(stores, embed.overview.progressionEquipment, 'equipment')
  const activeSkills = useEmbed(stores, embed.overview.skills, 'skills', 'skills-video-display')
  const [activeSkillsStep, setActiveSkillsStep] = React.useState(activeSkills?.active ?? activeSkills?.embed.step ?? 0)

  // Completed milestones, take from user metadata
  const [showCompleted, setShowCompleted] = React.useState(false)
  const metadataKey = `poe-leveling:${metadataId}:milestones`
  const [completedMilestones, setCompleted] = useLevelingMetadata(metadataKey)
  const toggleCompleted = React.useCallback(
    (id: string) =>
      setCompleted((prev) => {
        const result = new Set(prev)
        if (result.has(id)) {
          result.delete(id)
        } else {
          result.add(id)
        }

        return result
      }),
    [setCompleted]
  )
  const completedMilestonesCount = React.useMemo(() => {
    if (!embed.milestones) return 0
    let count = 0
    for (const milestone of embed.milestones) {
      if ('items' in milestone) {
        for (const item of milestone.items) {
          if (completedMilestones.has(item.id)) count += 1
        }
      } else if (completedMilestones.has(milestone.id)) {
        count += 1
      }
    }
    return count
  }, [embed.milestones, completedMilestones])

  // Updated signals used to highlight milestones
  const [updateSignals, setUpdateSignals] = React.useState<Record<string, number>>({})
  const signalMilestone = React.useCallback(
    (milestoneId: string) =>
      setUpdateSignals((signals) => {
        signals = { ...signals }
        signals[milestoneId] = (signals[milestoneId] ?? 0) + 1
        return signals
      }),
    []
  )

  // Active equipment embed
  const activeEquipment = React.useMemo(() => {
    const milestones = numberedMilestones
      .flatMap((group) => ('items' in group ? group.items : group))
      .filter((milestone) => completedMilestones.has(milestone.id))

    const customEmbed: EquipmentEmbed = _.cloneDeep({
      ...(progressionEquipmentEmbed?.embed ?? EmbedStore.equipment)
    })

    for (const milestone of milestones) {
      if (milestone.equipmentItems) {
        for (const slot of Object.keys(milestone.equipmentItems) as InventorySlotType[]) {
          if (milestone.equipmentItems[slot]) {
            customEmbed.items[slot] = milestone.equipmentItems[slot]
          }
        }
      }
    }

    return customEmbed
  }, [progressionEquipmentEmbed, numberedMilestones, completedMilestones])

  // Filters
  const [searchQuery, setSearchQuery] = React.useState('')
  const filteredMilestones = React.useMemo(
    () =>
      numberedMilestones.map((group): NumberedMilestone | NumberedMilestoneGroup => {
        let isGroupVisible = isStringInObject(searchQuery, group)

        if (isGroupVisible) {
          return { ...group, visible: true }
        } else if (!('items' in group)) {
          return { ...group, visible: false }
        } else {
          const filteredGroup: NumberedMilestoneGroup = {
            ...group,
            items: [],
            visible: false
          }

          for (const milestone of group.items) {
            const isMilestoneVisible =
              isStringInObject(searchQuery, milestone) ||
              (milestone.content != null &&
                isStringInObject(searchQuery, {
                  text: JSON.stringify(milestone.content.root)
                }))

            if (isMilestoneVisible) filteredGroup.visible = true
            filteredGroup.items.push({
              ...milestone,
              visible: isMilestoneVisible
            })
          }

          return filteredGroup
        }
      }),
    [searchQuery, numberedMilestones]
  )

  const [showPassiveTree, setShowPassiveTree] = React.useState(false)
  const [renderTree, setRenderTree] = React.useState(false)
  const updateShowPassiveTree = (newValue: boolean) => {
    setShowPassiveTree(newValue)
    setTimeout(() => {
      setRenderTree(true)
    }, 100)
  }

  const onMilestoneNavClick = React.useCallback(
    (milestoneId: string) => {
      if (completedMilestones.has(milestoneId)) {
        setShowCompleted(true)
      }

      setTimeout(() => {
        signalMilestone(milestoneId)
        if (typeof document !== 'undefined') {
          const milestoneElem = document.getElementById(`milestone-${milestoneId}`)
          if (milestoneElem) {
            scrollIntoView(milestoneElem, {
              behavior: 'smooth',
              scrollMode: 'if-needed',
              inline: 'nearest',
              block: 'nearest'
            })
          }
        }
      }, 100)
    },
    [completedMilestones, signalMilestone]
  )

  return (
    <Widget title={title} className="poe-progression-tab">
      <div className={classNames('poe-panels')}>
        <div className={classNames('poe-panel poe-progression-panel')}>
          <div className="poe-progression-content">
            <div className="poe-progression-nav-wrapper">
              <LevelingTitleBox type="sm" embed={embed} onEmbedChange={onEmbedChange} stores={stores} />

              <div className="poe-milestone-progression">
                <span>
                  Completed: {completedMilestonesCount}/{milestonesCount}
                </span>
              </div>

              <ul className={classNames('poe-progression-navigation', !!onEmbedChange && 'poe-can-reorder')}>
                {!!onEmbedChange && editMilestones ? (
                  <>
                    {filteredMilestones.map((group) =>
                      'items' in group ? (
                        <ProgressionNavGroupEditable
                          key={group.id}
                          group={group}
                          signalMilestone={signalMilestone}
                          onEmbedChange={onEmbedChange}
                        />
                      ) : (
                        <ProgressionNavItemEditable
                          key={group.id}
                          milestone={group}
                          signalMilestone={signalMilestone}
                          onEmbedChange={onEmbedChange}
                        />
                      )
                    )}
                    <li className={classNames('poe-milestone-group')}>
                      <span>
                        <button
                          onClick={() => {
                            onEmbedChange((embed) => {
                              if (embed.type !== 'leveling') return embed
                              embed = { ...embed }
                              embed.milestones = embed.milestones?.slice() ?? []
                              embed.milestones.push({
                                id: getUniqueId(),
                                title: 'Enter Group Name',
                                items: []
                              })
                              return embed
                            })
                          }}>
                          Add Group
                        </button>
                      </span>
                    </li>
                  </>
                ) : (
                  <>
                    {filteredMilestones.map((group) =>
                      'items' in group ? (
                        <ProgressionNavGroup
                          key={group.id}
                          group={group}
                          signalMilestone={signalMilestone}
                          completedMilestones={completedMilestones}
                          onMilestoneClick={onMilestoneNavClick}
                        />
                      ) : (
                        <ProgressionNavItem
                          key={group.id}
                          milestone={group}
                          signalMilestone={signalMilestone}
                          completedMilestones={completedMilestones}
                          onMilestoneClick={onMilestoneNavClick}
                        />
                      )
                    )}
                  </>
                )}
              </ul>

              <div className="poe-progression-mobile-nav">
                <Select
                  className="poe-commons-lib-select"
                  placeholder="Select a milestone..."
                  options={filteredMilestones
                    .flatMap((group) => ('items' in group ? group.items : group))
                    .filter(
                      (milestone) =>
                        milestone.visible !== false && (showCompleted || !completedMilestones.has(milestone.id))
                    )
                    .map((milestone) => ({
                      value: milestone.id,
                      label: `${milestone.index}. ${milestone.title}`
                    }))}
                  onChange={(option) => {
                    if (!option) return
                    signalMilestone(option.value)
                    if (typeof document !== 'undefined') {
                      if (completedMilestones.has(option.value)) return
                      const milestoneElem = document.getElementById(`milestone-${option.value}`)
                      if (milestoneElem) {
                        scrollIntoView(milestoneElem, {
                          behavior: 'smooth',
                          scrollMode: 'if-needed',
                          inline: 'nearest',
                          block: 'nearest'
                        })
                      }
                    }
                  }}
                />
              </div>
            </div>

            <div className="poe-progression-milestones">
              <div className="poe-milestones-top-bar-wrapper">
                <div className="poe-milestones-top-bar">
                  <div>
                    <Checkbox
                      type="sm"
                      value={showCompleted}
                      onChange={() => {
                        setShowCompleted(!showCompleted)
                      }}
                    />
                    <span>Show Completed</span>
                  </div>

                  {!!onEmbedChange && (
                    <button
                      className="poe-edit-milestones-button"
                      onClick={() => {
                        setEditMilestones(!editMilestones)
                      }}>
                      {editMilestones ? 'Done Editing Milestones' : 'Edit Milestones'}
                    </button>
                  )}

                  <div className="poe-progression-filters">
                    <span></span>
                    <Input
                      value={searchQuery}
                      onChange={(newValue) => {
                        setSearchQuery(newValue)
                      }}
                      placeholder="Search"
                    />
                  </div>
                </div>
              </div>

              <div className="poe-progression-content-scroll-wrapper">
                <ScrollBox
                  className={classNames('poe-progression-content-inner', showCompleted && 'poe-show-completed')}
                  innerClassName="poe-scrollbox-inner">
                  {filteredMilestones.map((group, index) =>
                    'items' in group ? (
                      group.items.map((item, index2) => (
                        <ProgressionMilestone
                          id={`milestone-${item.id}`}
                          key={`${index}-${index2}`}
                          milestone={item}
                          completed={completedMilestones.has(item.id)}
                          toggleCompleted={toggleCompleted}
                          updateSignal={updateSignals[item.id]}
                          embed={embed}
                          onEmbedChange={onEmbedChange}
                          isActiveTab={isActiveTab}
                        />
                      ))
                    ) : (
                      <ProgressionMilestone
                        id={`milestone-${group.id}`}
                        key={`${index}`}
                        milestone={group}
                        completed={completedMilestones.has(group.id)}
                        toggleCompleted={toggleCompleted}
                        updateSignal={updateSignals[group.id]}
                        embed={embed}
                        onEmbedChange={onEmbedChange}
                        isActiveTab={isActiveTab}
                      />
                    )
                  )}
                </ScrollBox>
              </div>
            </div>
          </div>
        </div>

        <div className={classNames('poe-panel poe-equipment-panel')}>
          <div className="poe-equipment-panel-content">
            <div className="paperdoll-widget">
              <span className="poe-title-box">Equipment</span>

              <Paperdoll items={activeEquipment.items} />
            </div>

            <Button
              className="poe-open-passives-skills"
              onClick={() => {
                updateShowPassiveTree(!showPassiveTree)
              }}>
              CHECK PASSIVE TREE
            </Button>
            <Modal
              className={showPassiveTree ? undefined : 'poe-leveling-tool-modal-hidden'}
              innerClassName={classNames('poe-passive-tree-wrapper')}
              onClose={() => {
                updateShowPassiveTree(false)
              }}>
              {renderTree && (
                <div className={classNames('poe-passive-tree-panel')}>
                  {activeTree && (
                    <WithStore store={activeTree.store}>
                      <PassiveTree
                        embed={activeTree.embed}
                        active={activeTree.active}
                        renderBackground={false}
                        bandits={embed.overview.bandits}
                      />
                    </WithStore>
                  )}
                </div>
              )}
            </Modal>

            <div className="poe-progression-skills">
              {activeSkills && (
                <WithStore store={activeSkills.store}>
                  <SkillGems
                    steps={activeSkills.embed.steps}
                    active={activeSkillsStep}
                    setActive={setActiveSkillsStep}
                  />
                </WithStore>
              )}
            </div>
          </div>
        </div>
      </div>
    </Widget>
  )
}
